import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { usePolicy } from '../../../../../../../../hooks/usePolicy'
import { useStatusReportContext } from '../../../../../statusReportContext'
import Comment from './Comment'
import CommentInput from './CommentInput'
import DeleteCommentConfirmModal from './DeleteCommentConfirmModal'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-6)',
    flex: 1,
    height: '100%',
    overflow: 'hidden'
  },
  commentsList: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-5)',
    overflow: 'auto'
  }
}))

function CommentList ({ comments = [], onAddComment, onEditComment, onDeleteComment, processing }) {
  const { report } = useStatusReportContext()
  const commentsAddPolicy = usePolicy('comments_add')
  const allowEditAllComments = usePolicy('comments_edit_any')
  const classes = useStyles()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const deleteModalRef = useRef(null)

  const reportActive = report?.status === 'active'

  const canAddComments = useMemo(() => {
    return reportActive && commentsAddPolicy
  }, [reportActive, commentsAddPolicy])

  // `comments_edit_any` bypasses the add policy here (matches the non-board
  // Comments organism). Per-comment authorship is still evaluated in Comment.jsx.
  const canModifyComments = useMemo(() => {
    return reportActive && (commentsAddPolicy || allowEditAllComments)
  }, [reportActive, commentsAddPolicy, allowEditAllComments])

  const handleAddSubmit = async (text) => {
    if (!text.trim() || isSubmitting) return

    setIsSubmitting(true)
    try {
      await onAddComment(text)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartEdit = useCallback((comment) => {
    // Functional setter so a stray click cannot overwrite an in-progress edit.
    setEditingCommentId((current) => current ?? comment.commentId)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingCommentId(null)
  }, [])

  const handleSaveEdit = useCallback(async (comment, body) => {
    if (!onEditComment) return
    await onEditComment(comment.commentId, body)
    setEditingCommentId(null)
  }, [onEditComment])

  const handleRequestDelete = useCallback((comment) => {
    deleteModalRef.current?.open(comment)
  }, [])

  const handleConfirmDelete = useCallback(async (commentId) => {
    if (!onDeleteComment) return
    if (editingCommentId === commentId) {
      setEditingCommentId(null)
    }
    await onDeleteComment(commentId)
  }, [onDeleteComment, editingCommentId])

  return (
    <div className={classes.container}>
      <div className={classes.commentsList}>
        {comments?.slice().sort((a, b) => {
          return new Date(a.createdDate) - new Date(b.createdDate)
        }).map((comment) => {
          const isEditingThis = editingCommentId === comment.commentId
          // Hide edit/delete on other comments while one is being edited.
          const canModifyThis = canModifyComments && (!editingCommentId || isEditingThis)
          return (
            <Comment
              key={comment.commentId}
              comment={comment}
              canModify={canModifyThis}
              isEditing={isEditingThis}
              isProcessing={processing}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              onDelete={handleRequestDelete}
            />
          )
        })}
        {!comments?.length && (
          <p className='text-neutral-500 font-size-xs text-center font-light py-10'>
            No comments yet.
          </p>
        )}
      </div>
      {canAddComments && !editingCommentId && (
        <CommentInput
          onSubmit={handleAddSubmit}
          disabled={isSubmitting || processing}
        />
      )}
      <DeleteCommentConfirmModal ref={deleteModalRef} onConfirm={handleConfirmDelete} />
    </div>
  )
}

CommentList.propTypes = {
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      commentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string,
      createdDate: PropTypes.string,
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      userName: PropTypes.string,
      userAvatar: PropTypes.string,
      avatarType: PropTypes.oneOf(['media', 'override', 'resource', 'public'])
    })
  ),
  onAddComment: PropTypes.func,
  onEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  processing: PropTypes.bool
}

export default CommentList
